import _ from 'lodash';

import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { ensureArray } from 'BMapsUtil/js_utils';
import { fetchPropertyGroupWithJobman, JobTypes } from 'BMapsSrc/jobman';
import { Compound } from 'BMapsModel';
import { createJob } from 'BMapsRedux/jobs/access';
import { calculateDockingBox } from 'BMapsSrc/data_tools';

export async function getGninaScoringProperties(compounds, { trigger }={}) {
    const chunks = chunkGninaCompounds(compounds);

    const gninaPromises = chunks.map(async (chunkCmpds) => {
        const { requestData, error } = await makeGninaRequest(chunkCmpds, { score_only: true });

        if (requestData) {
            const request = {
                jobtype: JobTypes.GNINA_SCORING,
                data: requestData,
            };
            await fetchPropertyGroupWithJobman(
                chunkCmpds, JobTypes.GNINA_SCORING, request, { trigger },
            );
        }
    });
    await Promise.all(gninaPromises);
}

export async function startGninaDocking(compounds, { boxParams, speedArg, ...otherParams }={}) {
    if (!boxParams && compounds.length > 0) {
        return [{ error: 'No box parameters provided for gnina docking' }];
    }

    const chunks = chunkGninaCompounds(compounds);

    const gninaPromises = chunks.map(async (chunkCmpds) => {
        let options = {};
        const wholeProteinDocking = !boxParams.refObj;
        if (!wholeProteinDocking) {
            const boxes = chunkCmpds.map((c) => (calculateDockingBox(App.Workspace, c, boxParams)));
            if (!boxes.every((b) => b.centroid.toString() === boxes[0].centroid.toString())) {
                console.warn(`startGninaDocking: different docking box centroids for compounds in same gnina request: ${boxes.map((b) => b.centroid).join('; ')}`);
            }
            const maxBox = _.maxBy(boxes, (b) => b.boxSize);
            const [cx, cy, cz] = maxBox.centroid;
            const edge = maxBox.boxSize;
            options = {
                center_x: cx, center_y: cy, center_z: cz, size_x: edge, size_y: edge, size_z: edge,
            };
        }
        const exhaustivenessMap = wholeProteinDocking
            ? { accurate: 128, fast: 64, faster: 8 }
            : { accurate: 64, /* fast: 8, */ faster: 4 }; // use gnina default (8) for fast
        options.exhaustiveness = exhaustivenessMap[speedArg];
        const otherGninaParams = ['minimize', 'scoring', 'cnn_scoring', 'cnn'];
        for (const gp of otherGninaParams) {
            const gpValue = otherParams[gp];
            if (gpValue != null && gpValue !== '') {
                options[gp] = gpValue;
            }
        }
        options.num_modes = otherParams.poseCount;
        const { requestData, error: requestError } = await makeGninaRequest(chunkCmpds, options);
        if (requestError) {
            return { jobResults: { error: requestError } };
        }
        const stash = {
            cmpdSmiles: chunkCmpds.reduce((acc, c) => {
                acc[c.resSpec] = { smiles: c.getUnnamedSmiles() };
                return acc;
            }, {}),
        };
        return {
            // { results, error }
            jobResults: await createJob(JobTypes.GNINA_DOCKING, requestData, stash),
            dockedCompounds: chunkCmpds,
        };
    });
    return Promise.all(gninaPromises);
}

/**
 * Chunk an array of compounds as an array of arrays,
 * ensuring that each chunk all has the same parent protein.
 * @param {Compound | Compound[]} compoundsIn
 * @returns {Compound[][]}
 */
function chunkGninaCompounds(compoundsIn, chunkSize=10) {
    const compounds = ensureArray(compoundsIn);
    const { dataParentsMap } = App.partitionByDataParents(compounds);
    return [...dataParentsMap.values()].flatMap((group) => _.chunk(group, chunkSize));
}

async function makeGninaRequest(compoundsIn, other) {
    const compounds = ensureArray(compoundsIn);
    const compound = compounds[0];
    if (!compound) {
        return { error: 'no compound provided for gnina scoring request' };
    }
    const { dataParentsList } = App.partitionByDataParents(compounds);
    if (dataParentsList.length > 1) {
        return { error: 'makeGninaRequest needs all compounds from the same protein structure' };
    }
    const { pdb, error, pdbObj } = await UserActions.GetProteinPdb(compound);
    if (pdbObj.residues.length === 0) {
        return { error: `no protein available for gnina scoring of ${compound.resSpec}` };
    } else if (error) {
        return { error };
    } else if (pdb) {
        const requestData = {
            receptor_pdb: pdb,
            ligand_sdf: Compound.makeSDF(
                compounds,
                { propInclusion: 'custom', customPropFn: (c) => ({ bmapsResSpec: c.resSpec }) },
            ),
            ...other,
        };
        return { requestData };
    } else {
        return { error: `no pdb data produced for ${compound.resSpec}` };
    }
}
